import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { initAccountStore } from "./account/accountStore";
import { startCloudSync } from "./stats/cloudSync";
import { applyVenue, getSelectedVenue } from "./theme/venues";
import { cleanUpLegacyThemes } from "./theme/themes";
import { clearLegacyEntitlements } from "./theme/entitlements";
import { sound } from "./audio/sound";
import { dismissSplash } from "./ui/splash";
import { warmRoutes } from "./ui/routes";
import { startFreshnessCheck } from "./ui/freshness";
import { startFeedbackOutbox } from "./feedback/outbox";
import { startRotationRealign } from "./ui/rotationRealign";
import { APP_ENV, APP_VERSION } from "./version";
// Gelasio is the card face (see pixi/cardArt.ts). Self-hosted rather than pulled
// from a CDN: local play is meant to work with no network at all, and a font
// that only arrives online would make the cards change shape offline.
import "@fontsource/gelasio/400.css";
import "@fontsource/gelasio/700.css";
// Fraunces (display) and Inter (body) are the UI chrome. They came from Google
// Fonts until the privacy policy needed "no third party sees a visitor's IP" to
// be true, so they are bundled too (docs/FONTS.md). `opsz.css` rather than the
// smaller `wght.css` for Fraunces: it keeps the optical-size axis the Google
// request asked for, which is what makes big headings cut finer than small
// labels. Inter was only ever asked for by weight.
import "@fontsource-variable/fraunces/opsz.css";
import "@fontsource-variable/inter/wght.css";
import "./ui/styles.css";

// Stamp the selected venue onto <html> before first paint so the material CSS
// variables (fabric/wood/paper/brass) are right from the very first frame.
applyVenue();
// One-time cleanup of decks made with the removed custom theme builder.
cleanUpLegacyThemes();
// One-time cleanup of the retired ?entitle= override's storage key. Being on the
// server's allowedHosts list is now the only way to hold a premium deck.
clearLegacyEntitlements();
// Arm the audio engine (it stays silent until the browser allows sound on first tap).
sound.boot(getSelectedVenue().id);
// Point the account store at Firebase and let the cloud save start following
// auth. Both only register listeners and return, so neither holds up the font
// gate below — the work they trigger happens once Firebase reports who is
// signed in, which is long after the first frame either way.
initAccountStore();
startCloudSync();
// A home-screen app is resumed, not relaunched; this is what notices a deploy.
startFreshnessCheck(APP_VERSION, APP_ENV);
// Feedback that could not be sent when it was written goes now, or on the backoff.
startFeedbackOutbox();
startRotationRealign();

/**
 * Wait for the card face before the first render.
 *
 * PixiJS rasterises a `Text` into a texture the moment it is constructed, using
 * whatever font the canvas can resolve *at that instant*. A `@font-face` is only
 * fetched lazily, when something first needs it, so without this the first cards
 * drawn would bake the fallback serif into their textures and keep it until
 * something happened to rebuild them — a font swap that never reaches the canvas.
 *
 * `document.fonts.ready` is not enough on its own: it resolves once nothing is
 * *pending*, and a face nobody has asked for yet was never pending. So ask for
 * the exact faces, with the digits that actually get printed.
 *
 * Bounded by a timeout, because a font is not worth a white screen. If the fetch
 * is slow or fails we render in the fallback rather than hang; the cards will
 * look wrong until a re-render, which beats not starting at all.
 */
const FONT_TIMEOUT_MS = 2000;

async function loadCardFont(): Promise<void> {
  if (!document.fonts?.load) return;
  const faces = ["700 40px Gelasio", "400 16px Gelasio"];
  const digits = "0123456789";
  await Promise.race([
    Promise.all(faces.map((f) => document.fonts.load(f, digits))).catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, FONT_TIMEOUT_MS)),
  ]);
}

void loadCardFont().then(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
  // Two frames: one for React's commit to reach the page, one for it to be
  // painted - the launch splash deals itself away to reveal that, not a blank.
  requestAnimationFrame(() => requestAnimationFrame(dismissSplash));
  primeTransitionCards();
  // Every screen's code, in the background, so no first visit waits on a fetch
  // mid-slide - then each menu screen built once, invisibly, so its first slide
  // does not pay the browser's first-render costs either. See `prewarmScreens`.
  void warmRoutes().then(() => import("./ui/prewarmScreens").then((m) => m.prewarmScreens()));
});

/**
 * Renders the deck a screen transition deals, on idle, once the app is up.
 *
 * The first navigation of a session used to draw a screen of blank cards: the
 * pack is only rendered on demand, and the demand arrived at the same instant it
 * was needed. Starting at launch means the cards are ready before anything can
 * ask for them.
 *
 * AFTER the render call and behind an idle callback, which is what keeps this
 * from being the thing it is preventing. It reaches for `pixi/cardPreview` via a
 * lazy import (see `ui/transitions/cardImages.ts`), and pulling the table
 * renderer in front of the first paint to decorate a navigation would be a much
 * worse trade than the blank cards.
 *
 * Nothing is downloaded that this session would not have downloaded anyway: the
 * first transition primed the same chunk, one navigation later. And a player who
 * has asked for reduced motion gets a cross-fade with no cards in it, so there
 * is nothing for them to preload.
 */
function primeTransitionCards(): void {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const start = () => void import("./ui/transitions/cardImages").then((m) => m.primeCardImages());
  // Reached off globalThis rather than through `in window`, which narrows the
  // else branch to `never` because the DOM lib does not declare it.
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  if (ric) ric(start, { timeout: 2000 });
  else window.setTimeout(start, 400);
}
